import { FieldGroup, TextField } from '../components/form';
import { Alert } from '../components/ui';
import type { Draft } from './draft';

/**
 * The things that outlive the person: their files, and anything they owned.
 * Ownership is the one that bites: a Team or shared mailbox with no owner is
 * nobody's problem until somebody needs it changed.
 */
export function StepHandover({ draft, onChange }: { draft: Draft; onChange: (next: Draft) => void }) {
  const set = <K extends keyof Draft['employee']>(key: K, value: Draft['employee'][K]) =>
    onChange({ ...draft, employee: { ...draft.employee, [key]: value } });

  const e = draft.employee;
  const nothingNamed = !e.dataHandoverTo && !e.sharedMailboxHandoverTo && !e.teamsHandoverTo;

  return (
    <div className="space-y-8">
      <FieldGroup
        title="Their files"
        description="OneDrive and personal drive contents. Without a name here it is deleted with the account."
      >
        <TextField
          label="Transfer files to"
          value={e.dataHandoverTo}
          onChange={(value) => set('dataHandoverTo', value)}
          hint="Name or email of whoever takes over their work."
          maxLength={160}
        />
      </FieldGroup>

      <FieldGroup
        title="What they owned"
        description="Ownership does not disappear with the account; it just stops having anybody attached to it."
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            label="Shared mailboxes to"
            value={e.sharedMailboxHandoverTo}
            onChange={(value) => set('sharedMailboxHandoverTo', value)}
            hint="Any shared mailbox they administered."
            maxLength={160}
          />

          <TextField
            label="Teams and SharePoint sites to"
            value={e.teamsHandoverTo}
            onChange={(value) => set('teamsHandoverTo', value)}
            hint="Anything where they were the only owner."
            maxLength={160}
          />
        </div>
      </FieldGroup>

      {nothingNamed && (
        <Alert tone="info">
          Nothing named yet. If this person genuinely owned nothing and kept no files worth keeping, that is a
          fine answer; leave it blank and say so in the notes, so it reads as a decision rather than an
          oversight.
        </Alert>
      )}
    </div>
  );
}
